import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page, Tree, FormControl } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useTheme,
    useToast,
    useResource,
    useModal,
    usePage,
    useTree,
} from "@/comn/hooks";
import {
    APIS,
    SF_ORGZ_SRCH,
    SG_SCTN_LIST,
    SF_ORGZ_INFO,
    URLS,
} from "@/tra/tancis/ptli/usr/orgz/services/PtliOrgzService";

/*
 * Organizations List
 * !== 조직 관리 목록 ==!
 */
export const PTLI0912001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { theme } = useTheme();
    const [root, setRoot] = useState(false);
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    const { getResource, updateResource } = useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "PTL0021" },
            { area: "orgz" },
            { area: "orgzSub" },
            { area: "cstmOrgzCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organizations List Search !== 조직 목록 검색 ==!
        orgzListSrch: useForm({
            defaultSchema: SF_ORGZ_SRCH,
            defaultValues: {
                langCd: theme.lang.toUpperCase(),
                ...(pgeStore?.form || {}),
            },
        }),

        // Organizations Info !== 조직 상세 ==!
        orgzInfo: useForm({
            defaultSchema: SF_ORGZ_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Section List !== 섹션 목록 ==!
        sctnList: useGrid({
            defaultSchema: SG_SCTN_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Organazations List !==  조직 목록 조회 ==!
        getOrgzList: useFetch({
            api: () => APIS.getOrgzList(form.orgzListSrch.getValues()),
            onSuccess: (data) => {
                const orgzList = data["orgzList"].content;
                tree.setData(handler.createTreeData(orgzList));

                tree.openTree();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }),
            showToast: true,
        }),
        // Check Organizations Information !== 조직 정보 조회 ==!
        getOrgzInfo: useFetch({
            api: (data) => APIS.getOrgzInfo(data.cstmOrgzId),
            onSuccess: (data) => {
                const orgzInfo = data.orgzInfo.content;
                form.orgzInfo.reset();
                form.orgzInfo.setValues(orgzInfo);
                form.orgzInfo.setValue("newYn", false);
                form.orgzInfo.setEditable(false);
                setRoot(false);

                //세관인 경우 수정 불가
                if (null !== orgzInfo.cstmOrgzTpCd) {
                    if ("S" !== orgzInfo.cstmOrgzTpCd) {
                        form.orgzInfo.setEditable(true);
                    }
                }

                if (orgzInfo.cstmOrgzCd === "ORGZ") {
                    setRoot(true);
                }

                form.orgzInfo.setEditable("cstmOrgzId", false);
                form.orgzInfo.setEditable("cstmOrgzTpCd", false);
                form.orgzInfo.setEditable("cstmOrgzNm", false);
            },
            onError: (error) => {},
        }),

        // Create Organizations !== 조직 등록 ==!
        createOrgz: useFetch({
            api: (data) => APIS.createOrgz({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        afterSave(data.orgzInfo.contentzInfo);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.orgzInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // Update Organizations !== 조직 수정 ==!
        updateOrgz: useFetch({
            api: (data) => APIS.updateOrgz({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        afterSave(data.orgzInfo.contentzInfo);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.orgzInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // delete Organizations !== 조직 삭제 ==!
        deleteOrgz: useFetch({
            api: (cstmOrgzId) => APIS.deleteOrgz(cstmOrgzId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00060", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onConfirm: () => {
                        form.orgzInfo.reset();
                        form.orgzInfo.setEditable(false);
                        fetch.getOrgzList.fetch();
                    },
                    close: false,
                });
            },
            onError: () => {},
            showToast: true,
        }),
        // Check for duplicate organizations !== 중복된 세관이나 유닛 체크 ==!
        getDuplCheck: useFetch({
            api: (data) => APIS.getDuplCheck(data),
            onSuccess(data) {
                const duplCheck = data.duplCheck.content;
                if (duplCheck) {
                    form.orgzInfo.setError("cstmOrgzCd", {
                        message: "msg.00005", // Duplicate data exists. !== 중복된 데이터가 존재합니다. ==!
                        type: "error",
                    });
                } else {
                    form.orgzInfo.clearErrors("cstmOrgzCd");
                }
            },
        }),
        getSctnList: useFetch({
            api: () => APIS.getSctnList(),
            onSuccess: (data: any) => {
                const { content } = data.sctnList;
                form.orgzListSrch.setSchema("cstmSctnCd", { options: content });
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };
    const render = {};

    const newInit = () => {
        form.orgzInfo.setEditable(true);

        form.orgzInfo.clearValues();

        form.orgzInfo.clearErrors();

        // form.orgzInfo.setEditable("cstmOrgzTpCd", false);
        // form.orgzInfo.setEditable("cstmOrgzNm", false);
        form.orgzInfo.setValue("newYn", true);
        setRoot(false);
    };

    const afterSave = (orgzInfo: any) => {
        //updateResource("orgz");
        fetch.getOrgzList.fetch();
        fetch.getOrgzInfo.fetch(orgzInfo);
    };
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: () => {
            fetch.getOrgzList.fetch();
        },

        createTreeData: (orgList: any[]): Record<string, any>[] => {
            const tempList: Record<string, any>[] = orgList.map((item: any) => {
                return {
                    name: comnUtils.replaceEmpty(item.cstmOrgzNm),
                    checkbox: false,
                    cstmOrgzId: comnUtils.replaceEmpty(item.cstmOrgzId),
                    cstmOrgzNm: comnUtils.replaceEmpty(item.cstmOrgzNm),
                    orgzLvl: comnUtils.replaceEmpty(item.orgzLvl, 0),
                    uprCstmOrgzId: comnUtils.replaceEmpty(item.uprCstmOrgzId),
                    uprCstmOrgzNm: comnUtils.replaceEmpty(item.uprCstmOrgzNm),
                    children: [],
                };
            });

            const map: Record<string, any> = {};
            tempList.forEach((item: Record<string, any>) => {
                map[item.cstmOrgzId] = item;
            });

            const treeData: Record<string, any>[] = [];
            tempList.forEach((item: Record<string, any>) => {
                if (item.uprCstmOrgzId && map[item.uprCstmOrgzId]) {
                    const parentNode = map[item.uprCstmOrgzId];
                    parentNode.children.push(item);
                } else {
                    treeData.push(item);
                }
            });

            return treeData;
        },

        //Click Organizations List Tree !== 조직 트리 클릭 ==!
        onTreeClick: (treeInfo: any) => {
            fetch.getOrgzInfo.fetch(treeInfo);
        },

        // Click New Button !== 신규 버튼 ==!
        onBtnNewClick: () => {
            newInit();
        },
        // Click Add Sub Organizations Button !== 신규 하위 조직 추가 버튼 ==!
        onBtnAddSubOrgzClick: () => {
            //조직유형이 Unit(U) 인 경우는 하위 조직 추가 불가
            if ("U" === form.orgzInfo.getValue("cstmOrgzTpCd")) {
                modal.openModal({ content: "msg.ptl.00722" }); // You cannot add sub-organizations of Unit. !== Unit 의 하위조직은 추가할 수 없습니다. ==!
                return;
            }

            let curCstmOrgzId = form.orgzInfo.getValue("cstmOrgzId");
            let cstmOrgzTpCd = form.orgzInfo.getValue("cstmOrgzTpCd");

            newInit();

            handler.getSubOrgzList(cstmOrgzTpCd);
            form.orgzInfo.setValue("uprCstmOrgzId", curCstmOrgzId);
        },

        // Sub organization COMBO search on parent detailed organization ID !== 상위상세조직ID에 하위 조직 COMBO 조회 ==!
        getSubOrgzList: (cstmOrgzTpCd: any) => {
            if (comnUtils.isUndefined(cstmOrgzTpCd) || cstmOrgzTpCd === "" || cstmOrgzTpCd === null) {
                cstmOrgzTpCd = "S";
            } else if (cstmOrgzTpCd === "S") {
                cstmOrgzTpCd = "C";
            } else {
                cstmOrgzTpCd = "U";
            }
            form.orgzInfo.setValue("cstmOrgzTpCd", cstmOrgzTpCd);
        },

        // Click Save Button !== 신규 저장 버튼 클릭  ==!
        onBtnSaveClick: () => {
            if (!comnUtils.isEmpty(form.orgzInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgzInfo.handleSubmit(
                () => {
                    const data = form.orgzInfo.getValues();
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createOrgz.fetch(data);
                        },
                        close: false,
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Click Edit Button !== 신규 저장 버튼 클릭  ==!
        onBtnEditClick: () => {
            if (!comnUtils.isEmpty(form.orgzInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgzInfo.handleSubmit(
                () => {
                    const data = form.orgzInfo.getValues();
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateOrgz.fetch(data);
                        },
                        close: false,
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Click Delete Button !== 삭제 버튼 클릭  ==!
        onBtnDeleteClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    fetch.deleteOrgz.fetch(form.orgzInfo.getValue("cstmOrgzId"));
                },
            });
        },
        // Check for duplicate organizations !== 중복된 세관이나 유닛 체크 ==!
        getDuplCheck: (data: any) => {
            const orgzInfo = form.orgzInfo.getValues();
            fetch.getDuplCheck.fetch(orgzInfo);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store)) {
            form.orgzListSrch.setValue("cstmOrgzNm", store.cstmOrgzNm);
            form.orgzListSrch.setValue("cstmOrgzTpCd", store.cstmOrgzTpCd);
            form.orgzListSrch.setValue("langCd", theme.lang);
        }

        fetch.getSctnList.fetch();
        handler.onSrchBtnClick();
        form.orgzInfo.setEditable(false);
        tree.tree._tree.current._checkbox = false;
    }, [theme.lang]);

    const newYn = form.orgzInfo.watch("newYn");
    const cstmOrgzTpCd = form.orgzInfo.watch("cstmOrgzTpCd");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.orgzListSrch.schema.cstmSctnCd}
                                popupParams={{ cstmOrgzTpCd: "S" }}
                            />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.orgzListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Layout gap={4}>
                <Group>
                    <Group.Header>
                        <Group.Title title={"T_ORGZ_LIST"} />
                    </Group.Header>
                    <Group.Body>
                        <Tree {...tree.tree} defaultOpen={true} size={12} height={500} onClick={handler.onTreeClick} />
                        <Layout>
                            <Layout.Right>
                                <Layout.Button role={"new"} onClick={handler.onBtnNewClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Label
                                    label={
                                        cstmOrgzTpCd == "U"
                                            ? "L_CSTM_OFCE_ID"
                                            : cstmOrgzTpCd == "C"
                                              ? "L_CSTM_SCTN_ID"
                                              : "L_UPR_CSTM_ORGZ_ID"
                                    }
                                    labelSize={3}
                                />
                                <Group.Col>
                                    <Layout>
                                        <FormControl
                                            {...form.orgzInfo.schema.uprCstmOrgzId}
                                            controlSize={12}
                                            callback={(data) => {
                                                data.hasOwnProperty("rowData")
                                                    ? handler.getSubOrgzList(data.rowData.cstmOrgzTpCd)
                                                    : handler.getSubOrgzList(data.cstmOrgzTpCd);
                                            }}
                                        />
                                    </Layout>
                                </Group.Col>
                            </Group.Row>
                            <Group.Row>
                                <Group.Label
                                    label={
                                        cstmOrgzTpCd == "U"
                                            ? "L_CSTM_UNIT_ID"
                                            : cstmOrgzTpCd == "C"
                                              ? "L_CSTM_OFCE_ID"
                                              : "L_CSTM_SCTN_ID"
                                    }
                                    labelSize={3}
                                />
                                <Group.Col>
                                    <Layout direction="row" gap={1}>
                                        <FormControl
                                            {...form.orgzInfo.schema.cstmOrgzCd}
                                            controlSize={8}
                                            popupParams={{ cstmOrgzTpCd: form.orgzInfo.getValue("cstmOrgzTpCd") }}
                                            onChange={handler.getDuplCheck}
                                        />
                                    </Layout>
                                </Group.Col>
                            </Group.Row>
                            <Group.Row hidden={newYn === true}>
                                <Group.Control
                                    {...form.orgzInfo.schema.cstmOrgzTpCd}
                                    excludes={newYn === true || cstmOrgzTpCd !== "C" ? ["C"] : []}
                                ></Group.Control>
                            </Group.Row>
                            {root && (
                                <Group.Row>
                                    <Group.Control {...form.orgzInfo.schema.cstmOrgzNm}></Group.Control>
                                </Group.Row>
                            )}
                        </Group.Section>
                        <Layout>
                            <Layout.Left hidden={newYn === true}>
                                <Layout.Button onClick={handler.onBtnAddSubOrgzClick}>
                                    {t("B_ADD_SUB_ORGZ")}
                                </Layout.Button>
                            </Layout.Left>
                            <Layout.Right hidden={!newYn}>
                                <Layout.Button role={"save"} onClick={handler.onBtnSaveClick} />
                            </Layout.Right>
                            <Layout.Right hidden={newYn === true || cstmOrgzTpCd == null || cstmOrgzTpCd === "S"}>
                                <Layout.Button role={"save"} onClick={handler.onBtnEditClick} />
                                <Layout.Button role={"delete"} onClick={handler.onBtnDeleteClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </Layout>
        </Page>
    );
};
